import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SorterResult } from 'antd/es/table/interface';
import type { BookingRow } from '@/models';
import {
  useGetBookingListQuery,
  useGetBookingSummaryQuery,
  useGetAwaitingClientOrderQuery,
  useGetCampaignTimelineQuery
} from '@/api';
import { usePagination } from '@/hooks/usePagination';
import { useSearch } from '@/hooks/useSearch';
import type {
  BookingFilterValues,
  BookingSortField,
  TimelineViewValues
} from '../BookingSheet.types';
import {
  currentQuarterKey,
  quarterRange
} from '../components/CampaignTimeline/useTimelineModel';

const DEFAULT_PAGE_SIZE = 20;

/** Opens on the quarter in progress: the booked range spans years, and the
 *  question asked of this page is almost always "what is happening now". */
const defaultView = (): TimelineViewValues => ({
  quarter: currentQuarterKey(),
  status: 'all',
  zoom: 'month',
  sort: 'start'
});

/* The last quarter picked, so a refresh or a return to the page keeps it.
   Without this the bar snapped back to the current quarter every time, and
   anyone working across all quarters had to re-pick it on each visit.
   Storage can be unavailable (private windows, blocked site data), so every
   access is guarded and the page falls back to the default. */
const QUARTER_STORAGE_KEY = 'bookingSheet.quarter';
const ALL_QUARTERS = '__all';

const readStoredQuarter = (): TimelineViewValues['quarter'] | undefined => {
  try {
    const stored = window.localStorage.getItem(QUARTER_STORAGE_KEY);
    if (stored === null) return undefined;
    return stored === ALL_QUARTERS ? null : stored;
  } catch {
    return undefined;
  }
};

const storeQuarter = (quarter: TimelineViewValues['quarter']) => {
  try {
    window.localStorage.setItem(QUARTER_STORAGE_KEY, quarter ?? ALL_QUARTERS);
  } catch {
    // Not remembered this time; the filter itself still applies.
  }
};

const initialView = (): TimelineViewValues => {
  const stored = readStoredQuarter();
  return stored === undefined ? defaultView() : { ...defaultView(), quarter: stored };
};

export const useBookingSheet = () => {
  const { page, limit, handlePaginationChange, resetPage } = usePagination({
    page: 1,
    limit: DEFAULT_PAGE_SIZE
  });
  const { search, searchInput, handleSearch: handleSearchState } = useSearch();
  const [filterValues, setFilterValuesState] = useState<BookingFilterValues>({});
  const [sort, setSort] = useState<BookingSortField | undefined>('won_date');
  const [order, setOrder] = useState<1 | -1>(-1);
  /* Lives here rather than inside the timeline so one filter bar at the top of
     the page can drive both tabs. */
  const [view, setViewState] = useState<TimelineViewValues>(initialView);

  useEffect(() => {
    storeQuarter(view.quarter);
  }, [view.quarter]);

  const handleSearch = useCallback(
    (value: string) => {
      handleSearchState(value);
      resetPage();
    },
    [handleSearchState, resetPage]
  );

  const setFilterValues = useCallback(
    (next: Partial<BookingFilterValues>) => {
      setFilterValuesState((prev) => ({ ...prev, ...next }));
      resetPage();
    },
    [resetPage]
  );

  const setView = useCallback(
    (patch: Partial<TimelineViewValues>) => {
      setViewState((prev) => ({ ...prev, ...patch }));
      /* Quarter and status narrow the query just as the filter bar does, so the
         page has to go back to 1 with them. Without this, changing quarter
         while deep in the results leaves the offset past the end of the new,
         smaller set — an empty table that looks like "no bookings" rather than
         "you are on page 20 of 4". */
      resetPage();
    },
    [resetPage]
  );

  const resetAllFilters = useCallback(() => {
    setFilterValuesState({});
    setViewState(defaultView());
    handleSearchState('');
    resetPage();
  }, [handleSearchState, resetPage]);

  const hasActiveFilterValues = useMemo(
    () => Object.values(filterValues).some((v) => !!v && v.trim() !== ''),
    [filterValues]
  );

  /* Reset is enabled by anything the bar can change, including the view
     controls -- otherwise a user who has zoomed and filtered to one quarter
     sees a disabled Reset and no way back. */
  const isViewDefault = useMemo(() => {
    const base = defaultView();
    return (
      view.quarter === base.quarter &&
      view.status === base.status &&
      view.zoom === base.zoom &&
      view.sort === base.sort
    );
  }, [view]);

  /* Quarter becomes a flight window and status goes through as-is; both are
     applied in SQL, so the table, the summary totals and the timeline all
     describe the same set of bookings rather than three different ones. */
  const viewArgs = useMemo(() => {
    const window = view.quarter ? quarterRange(view.quarter) : null;
    return {
      ...(window
        ? {
            flightFrom: window.start.format('YYYY-MM-DD'),
            flightTo: window.end.format('YYYY-MM-DD')
          }
        : {}),
      ...(view.status !== 'all' ? { status: view.status } : {})
    };
  }, [view.quarter, view.status]);

  const queryArgs = useMemo(
    () => ({
      page,
      pageSize: limit,
      ...(search ? { search } : {}),
      ...(filterValues.channel ? { channel: filterValues.channel } : {}),
      ...(filterValues.platform ? { platform: filterValues.platform } : {}),
      ...(filterValues.country ? { country: filterValues.country } : {}),
      ...(filterValues.buyModel ? { buyModel: filterValues.buyModel } : {}),
      ...viewArgs,
      /* One row per campaign, counted over campaigns: the table displays
         campaigns, so paging it over line items offered pages that did not
         exist (416 line items became 21 pages of a 3-campaign result). */
      groupByCampaign: true,
      ...(sort ? { sort, order } : {})
    }),
    [page, limit, search, filterValues, viewArgs, sort, order]
  );

  const summaryArgs = useMemo(
    () => ({
      ...(search ? { search } : {}),
      ...(filterValues.channel ? { channel: filterValues.channel } : {}),
      ...(filterValues.platform ? { platform: filterValues.platform } : {}),
      ...(filterValues.country ? { country: filterValues.country } : {}),
      ...(filterValues.buyModel ? { buyModel: filterValues.buyModel } : {}),
      ...viewArgs
    }),
    [search, filterValues, viewArgs]
  );

  const {
    data: bookingList,
    isLoading,
    isFetching,
    isError
  } = useGetBookingListQuery(queryArgs, { refetchOnMountOrArgChange: true });

  const {
    data: summary,
    isLoading: isSummaryLoading,
    /* isFetching rather than isLoading, so the figures also show as pending
       when a filter changes — the previous answer is no longer the answer. */
    isFetching: isSummaryFetching,
    isError: isSummaryError
  } = useGetBookingSummaryQuery(summaryArgs, { refetchOnMountOrArgChange: true });

  // The timeline shares the table's filters, so both tabs always describe the
  // same set of bookings.
  const {
    data: timeline,
    isLoading: isTimelineLoading,
    isError: isTimelineError
  } = useGetCampaignTimelineQuery(summaryArgs, { refetchOnMountOrArgChange: true });

  /* Deliberately unfiltered: a held booking the current filters exclude is
     still held, and the point of the banner is to surface what the page is not
     showing. */
  const { data: awaiting } = useGetAwaitingClientOrderQuery();

  const handleSort = useCallback(
    (sorter: SorterResult<BookingRow>) => {
      const { columnKey, order: sortOrder } = sorter;

      if (columnKey && sortOrder) {
        setSort(columnKey as BookingSortField);
        setOrder(sortOrder === 'ascend' ? 1 : -1);
      } else {
        setSort(undefined);
        setOrder(-1);
      }
      resetPage();
    },
    [resetPage]
  );

  const rows = bookingList?.rows ?? [];
  const total = bookingList?.total ?? 0;

  /**
   * "No bookings yet" (empty table, zero filters applied) is a different state from
   * "no rows match your filters" (empty table, some filter/search active) — only the
   * former should look like a genuinely empty dataset.
   */
  const isFiltered =
    Boolean(search?.trim()) || hasActiveFilterValues || !isViewDefault;

  return {
    rows,
    total,
    summary,
    awaiting,
    isLoading,
    isFetching,
    isError,
    isSummaryLoading,
    isSummaryError,
    isSummaryFetching,
    timeline,
    isTimelineLoading,
    isTimelineError,
    page,
    limit,
    handlePaginationChange,
    search,
    searchInput,
    handleSearch,
    filterValues,
    setFilterValues,
    view,
    setView,
    isViewDefault,
    resetAllFilters,
    hasActiveFilterValues,
    isFiltered,
    handleSort,
    sort,
    order
  };
};
